// src/components/ProductManagement.jsx

// ************************************************************
// SECCIÓN: IMPORTACIONES Y CONFIGURACIÓN DEL COMPONENTE
// ************************************************************
import React, { useState, useEffect } from 'react';
import api from '../api/api';
import '../styles/ProductManagement.css';

// ************************************************************
// SECCIÓN: COMPONENTE PRODUCT MANAGEMENT
// ************************************************************
const ProductManagement = () => {
  // Estado para almacenar los productos
  const [products, setProducts] = useState([]);
  // Estado para almacenar la cadena de búsqueda
  const [searchQuery, setSearchQuery] = useState('');
  // Estado para controlar la visibilidad del modal y producto en edición (si existe)
  const [modalOpen, setModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  // Estado del formulario
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    stock: '',
    imageUrl: '',
    available: true,
  });

  // ************************************************************
  // SECCIÓN: CICLO DE VIDA - OBTENER PRODUCTOS
  // ************************************************************
  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const res = await api.get('/api/products');
      setProducts(res.data);
    } catch (err) {
      console.error('Error al obtener productos:', err);
    }
  };

  // ************************************************************
  // SECCIÓN: MÉTODOS PARA ABRIR/CERRAR MODAL Y MANIPULAR FORMULARIO
  // ************************************************************
  const handleOpenModal = (product = null) => {
    if (product) {
      // Modo edición
      setEditingProduct(product);
      setFormData({ ...product });
    } else {
      // Modo creación
      setEditingProduct(null);
      setFormData({
        name: '',
        description: '',
        price: '',
        stock: '',
        imageUrl: '',
        available: true,
      });
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingProduct(null);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  // ************************************************************
  // SECCIÓN: MÉTODO DE ENVÍO DEL FORMULARIO (CREAR O EDITAR)
  // ************************************************************
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingProduct) {
        // Actualizar producto existente
        await api.put(`/api/products/${editingProduct.id}`, formData);
      } else {
        // Crear nuevo producto
        await api.post('/api/products', formData);
      }
      await fetchProducts();
      handleCloseModal();
    } catch (err) {
      console.error('Error al guardar producto:', err);
      alert('Error al guardar el producto');
    }
  };

  // ************************************************************
  // SECCIÓN: MÉTODO PARA BORRAR UN PRODUCTO
  // ************************************************************
  const handleDelete = async (id) => {
    if (window.confirm('¿Estás seguro de eliminar este producto?')) {
      try {
        await api.delete(`/api/products/${id}`);
        await fetchProducts();
      } catch (err) {
        console.error('Error al eliminar producto:', err);
        alert('Error al eliminar el producto');
      }
    }
  };

  // ************************************************************
  // SECCIÓN: FILTRADO DE PRODUCTOS POR BÚSQUEDA
  // ************************************************************
  const filteredProducts = products.filter((product) =>
    (product.name || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  // ************************************************************
  // SECCIÓN: RENDERIZADO DEL COMPONENTE
  // ************************************************************
  return (
    <div className="product-management">
      {/* HEADER CON TÍTULO, BARRA DE BÚSQUEDA Y BOTÓN PARA AÑADIR PRODUCTO */}
      <header className="pm-header">
        <h1>Gestión de Productos</h1>
        <div className="pm-search-bar">
          <input
            type="text"
            className="pm-search-input"
            placeholder="Buscar Producto..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <button className="pm-search-button" type="button">
            Buscar
          </button>
        </div>

        {/* Contenedor para el botón */}
        <div className="pm-header-actions">
          <button className="add-button" onClick={() => handleOpenModal()} type="button">
            Añadir Producto
          </button>
        </div>
      </header>

      {/* GRID DE PRODUCTOS */}
      <div className="pm-grid">
        {filteredProducts.map((product) => (
          <div key={product.id} className="pm-card">
            <img
              src={product.imageUrl ? product.imageUrl : 'https://via.placeholder.com/150'}
              alt={product.name}
              className="pm-card-image"
            />
            <div className="pm-card-body">
              <h3>{product.name}</h3>
              <p className="pm-price">${product.price}</p>
              <p>Stock: {product.stock}</p>
              <div className="pm-card-actions">
                <button type="button" onClick={() => handleOpenModal(product)}>
                  Editar
                </button>
                <button type="button" onClick={() => handleDelete(product.id)}>
                  Eliminar
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* MODAL PARA AÑADIR/EDITAR PRODUCTO */}
      {modalOpen && (
        <div className="pm-modal-overlay">
          <div className="pm-modal">
            <h2>{editingProduct ? 'Editar Producto' : 'Añadir Producto'}</h2>
            <form onSubmit={handleSubmit} className="pm-form">
              <label>
                Nombre:
                <input type="text" name="name" value={formData.name} onChange={handleChange} required />
              </label>

              <label>
                Descripción:
                <textarea name="description" value={formData.description} onChange={handleChange} />
              </label>

              <label>
                Precio:
                <input
                  type="number"
                  step="0.01"
                  name="price"
                  value={formData.price}
                  onChange={handleChange}
                  required
                />
              </label>

              <label>
                Stock:
                <input type="number" name="stock" value={formData.stock} onChange={handleChange} required />
              </label>

              <label>
                URL de imagen:
                <input type="text" name="imageUrl" value={formData.imageUrl} onChange={handleChange} />
              </label>

              <label>
                Disponible:
                <input type="checkbox" name="available" checked={!!formData.available} onChange={handleChange} />
              </label>

              <div className="pm-form-actions">
                <button type="submit">{editingProduct ? 'Actualizar' : 'Crear'}</button>
                <button type="button" onClick={handleCloseModal}>
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductManagement;
